/**
 * TASK-833 — the disposition footer, from server policy, in one place.
 *
 * Moved out of `SignalInvestigation` (retired by this ticket) rather than rewritten: the
 * rationale collection, the "the SERVER remains the enforcer" contract and the structured
 * refusal are TASK-732's and already reviewed. Two things are added, both from TASK-820:
 *
 *   * `recommended_disposition` is MARKED, never made the only button. DEC-3's basis is that
 *     the advisor approves and is therefore accountable, and an advisor with one button has
 *     not approved anything.
 *   * a deliberately ABSENT disposition shows the catalogue's own reason
 *     (`absent_disposition_notes`), authored by the owner and rendered `t(key, text)`. This
 *     surface writes none of that copy — the whole point of DEC-1 was that the explanation
 *     travels with the policy that enforces it.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import type { ActionPolicy } from '../../types/dashboard';
import { policyPermits } from '../../utils/signalPolicy';
import { useViewerRole } from '../../contexts/AccountContext';

/**
 * Dispositions this footer renders. `investigate` IS this surface, and
 * `read`/`assign`/`snooze`/`dismiss` live on the alert list — so what remains here are the
 * terminal judgments. Kept as an allow-list rather than a deny-list so a disposition added
 * to the catalogue does not silently appear as an unhandled button.
 */
const TERMINAL_DISPOSITIONS = ['escalate', 'resolve', 'propose_trade'] as const;

const DEFAULT_LABEL: Record<string, string> = {
  escalate: 'Escalate',
  resolve: 'Resolve',
  propose_trade: 'Propose trade',
};

export interface SignalActionsProps {
  policy?: ActionPolicy | null;
  /**
   * TASK-732: execute a policy judgment. The footer collects the rationale the policy
   * requires; the SERVER remains the enforcer — a refusal rejects the promise and is shown,
   * never swallowed.
   */
  onPolicyAction?: (id: string, rationale: string) => Promise<void> | void;
}

export const SignalActions: React.FC<SignalActionsProps> = ({ policy, onPolicyAction }) => {
  const { t } = useTranslation('dashboard');
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [rationale, setRationale] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionDone, setActionDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // TASK-923: role-gated. `escalate` and `resolve` are compliance-only on every compliance
  // row, and this list offered them to whoever was looking.
  const role = useViewerRole();
  const actions = TERMINAL_DISPOSITIONS.filter((id) => policyPermits(policy, id, role));
  const recommended = policy?.recommended_disposition ?? null;
  const notes = policy?.absent_disposition_notes ?? [];

  const rationaleRequired = (id: string) => policy?.rationale?.[id] === 'required';

  const runAction = async (id: string, text: string) => {
    if (!onPolicyAction) return;
    setBusy(true);
    setActionError(null);
    try {
      await onPolicyAction(id, text.trim());
      setActiveAction(null);
      setRationale('');
      setActionDone(id);
    } catch (e: unknown) {
      // The server's structured refusal (code, policy_id, reason) — shown, never swallowed.
      const detail = (e as { response?: { data?: { detail?: { reason?: string } } } })
        ?.response?.data?.detail;
      setActionError(detail?.reason ?? t('radar.actions.failed', 'The server refused this action.'));
    } finally {
      setBusy(false);
    }
  };

  if (!policy) {
    // TASK-722: no policy is not "no actions" — it is not knowing which are allowed, and a
    // bare footer would read as the former.
    return (
      <p data-testid="signal-actions-nopolicy" className="text-xs text-gray-500">
        {t('radar.actions.policyUnavailable',
           'The action policy for this signal is unavailable, so no actions are offered.')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((id) => (
          <button
            key={id}
            type="button"
            data-testid={`dossier-action-${id}`}
            disabled={!onPolicyAction || busy}
            onClick={() => {
              setActionError(null);
              if (rationaleRequired(id)) {
                setActiveAction(activeAction === id ? null : id);
              } else {
                void runAction(id, rationale);
              }
            }}
            className={
              'rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-40 ' +
              (id === recommended
                ? 'border-[#002B45] bg-[#002B45] text-white hover:bg-[#003d63]'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50')
            }
          >
            {t(`radar.actions.${id}`, DEFAULT_LABEL[id] ?? id)}
            {id === recommended && (
              // Marked, not decided. The advisor still chooses.
              <span data-testid={`dossier-recommended-${id}`} className="ml-1 font-normal opacity-80">
                · {t('radar.actions.suggested', 'suggested')}
              </span>
            )}
          </button>
        ))}
        {actions.length === 0 && (
          <p data-testid="dossier-no-actions" className="text-xs text-gray-500">
            {t('radar.actions.noneAllowed', 'This signal has no judgment action to take here.')}
          </p>
        )}
      </div>

      {/* Why a disposition is deliberately absent — the catalogue's words, not this file's. */}
      {notes.map((note) => (
        <p
          key={note.disposition}
          data-testid={`dossier-absent-${note.disposition}`}
          className="flex items-start gap-1.5 text-xs text-gray-500"
        >
          <Info className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" aria-hidden />
          {t(note.key, note.text)}
        </p>
      ))}

      {activeAction && (
        <div data-testid="dossier-rationale" className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
          <label className="block text-xs font-medium text-gray-700" htmlFor="dossier-rationale-input">
            {t('radar.actions.rationaleRequired', 'This action requires a written rationale.')}
          </label>
          <textarea
            id="dossier-rationale-input"
            data-testid="dossier-rationale-input"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={2}
            className="w-full rounded border border-gray-300 p-2 text-xs"
            placeholder={t('radar.actions.rationalePlaceholder', 'Why is this the right call?')}
          />
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="dossier-rationale-confirm"
              disabled={!rationale.trim() || busy}
              onClick={() => void runAction(activeAction, rationale)}
              className="rounded bg-[#002B45] px-2 py-1 text-xs font-medium text-white disabled:opacity-40"
            >
              {t('radar.actions.confirm', 'Confirm')}
            </button>
            <button
              type="button"
              onClick={() => { setActiveAction(null); setActionError(null); }}
              className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
            >
              {t('radar.actions.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {/* aria-live: decision outcomes are announced, not merely painted. */}
      <div aria-live="polite">
        {actionError && (
          <p data-testid="dossier-action-error" className="text-xs text-red-700">{actionError}</p>
        )}
        {actionDone && !actionError && (
          <p data-testid="dossier-action-done" className="text-xs text-emerald-700">
            {t('radar.actions.done', 'Action recorded.')}
          </p>
        )}
      </div>
    </div>
  );
};

export default SignalActions;
